import React from 'react';
import WrapPopover, { PopoverGapType, PopoverPaddingType, PopoverWidthType } from './WrapPopover';
import { VerticalListButton, ButtonVariant, BUTTON_VARIANTS } from './VerticalListButton';
import { CategoryItem } from '../CategoryHorizontalScroller';
import Badge from '@/components/Badge';

export interface ButtonListObject {
  icon: JSX.Element;
  txt: string;
  value: any;
  badge?: string;
  rightElement?: JSX.Element;
  onClick?: () => void;
  closePopoverOnClick?: boolean;
  /** Renders a check on the row (unless it already has a `rightElement`/`badge`). */
  selected?: boolean;
}

interface ButtonsListPopoverProps {
  objects: ButtonListObject[];
  onSelectItem: (value: any) => void;
  width?: PopoverWidthType;
  minWidth?: number;
  maxWidth?: number;
  height?: number;
  maxHeight?: number;
  buttonRef?: React.RefObject<HTMLElement>;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  title?: string;
  showCloseButton?: boolean;
  onClose?: () => void;
  variant?: ButtonVariant;
  categories?: CategoryItem[];
}

const VARIANT_GAPS: Record<ButtonVariant, { gap: PopoverGapType; itemGap: number; padding: PopoverPaddingType }> = {
  'menu':               { gap: 4, itemGap: 4, padding: '12,16,16,16' },
  'menu-danger':               { gap: 4, itemGap: 4, padding: '12,16,16,16' },
  'suggest':            { gap: 8, itemGap: 8, padding: '12,16,16,16' },
}

interface ListItemProps {
  item: ButtonListObject;
  onSelect: () => void;
  variant: ButtonVariant;
}

const PopoverListItem: React.FC<ListItemProps> = ({ item, onSelect, variant }) => {
  const rightEl = item.badge
    ? <Badge text={item.badge} mainStyle="info" />
    : item.rightElement
  return <VerticalListButton variant={variant} text={item.txt} icon={item.icon} rightElement={rightEl} selected={item.selected} onClick={onSelect} />
}

const ButtonsListPopover: React.FC<ButtonsListPopoverProps> = ({
  objects,
  onSelectItem,
  width,
  minWidth,
  maxWidth,
  height,
  maxHeight,
  buttonRef,
  placement,
  title,
  showCloseButton,
  onClose,
  variant = 'suggest',
  categories,
}) => {
  const { gap, itemGap, padding } = VARIANT_GAPS[variant]
  return (
    <WrapPopover
      width={width}
      minWidth={minWidth}
      maxWidth={maxWidth}
      height={height}
      maxHeight={maxHeight}
      buttonRef={buttonRef}
      placement={placement}
      padding={padding}
      gap={gap}
      title={title}
      showCloseButton={showCloseButton}
      onClose={onClose}
      categories={categories}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: `${itemGap}px` }}>
        {objects.length === 0
          ? Array.from({ length: 6 }).map((_, index) => {
              return <VerticalListButton variant={variant} disabled />
            })
          : objects.map((item, index) => {
              const handleSelect = () => {
                if (item.onClick) {
                  item.onClick()
                  if (item.closePopoverOnClick) onClose?.()
                } else {
                  onSelectItem(item.value)
                }
              }
              return <PopoverListItem key={index} item={item} onSelect={handleSelect} variant={variant} />
            })}
      </div>
    </WrapPopover>
  );
};

export default ButtonsListPopover;
